import type RealtimeKitClient from "@cloudflare/realtimekit";

type SelfWithSpeakerApi = RealtimeKitClient["self"] & {
    setupSpeaker?: () => Promise<void>;
    getSpeakerDevices?: () => Promise<MediaDeviceInfo[]>;
    setSpeakerDevice?: (
        device: MediaDeviceInfo,
        opts: { saveDevicePreference: boolean },
    ) => Promise<void>;
    setDevice?: (device: MediaDeviceInfo) => Promise<void>;
    getCurrentDevices?: () => {
        audio?: MediaDeviceInfo;
        video?: MediaDeviceInfo;
        speaker?: MediaDeviceInfo;
    };
};

type AudioSessionNavigator = Navigator & {
    audioSession?: { type?: string };
};

export type MobileSpeakerOptions = {
    /** iOS: pulso mic / audioSession após gesto do usuário (re-rota áudio). */
    iosMicPulse?: boolean;
    root?: ParentNode | null;
};

export type SpeakerBoostResult = {
    ok: boolean;
    mediaElements: number;
    played: number;
    audioSessionSet: boolean;
    speakerDeviceSet: boolean;
    sinkIdApplied: number;
    iosPulse: boolean;
    message: string;
};

/**
 * RTK renderiza `<audio>`/`<video>` dentro de Shadow DOM.
 * querySelectorAll no light DOM NÃO enxerga esses elementos — bug do boost antigo.
 */
export function collectMediaElementsDeep(root: ParentNode | null): HTMLMediaElement[] {
    if (!root) {
        return [];
    }

    const results: HTMLMediaElement[] = [];
    const seen = new Set<HTMLMediaElement>();

    const visit = (node: ParentNode) => {
        const list =
            typeof (node as ParentNode & { querySelectorAll?: typeof document.querySelectorAll })
                .querySelectorAll === "function"
                ? Array.from(
                      (node as Document | Element | ShadowRoot).querySelectorAll(
                          "audio, video",
                      ),
                  )
                : [];

        for (const el of list) {
            const media = el as HTMLMediaElement;
            if (!seen.has(media)) {
                seen.add(media);
                results.push(media);
            }
        }

        const all =
            typeof (node as ParentNode & { querySelectorAll?: typeof document.querySelectorAll })
                .querySelectorAll === "function"
                ? Array.from((node as Document | Element | ShadowRoot).querySelectorAll("*"))
                : [];

        for (const el of all) {
            if (el.shadowRoot) {
                visit(el.shadowRoot);
            }
        }
    };

    visit(root);
    return results;
}

function resolveBoostRoot(root?: ParentNode | null): ParentNode | null {
    if (typeof document === "undefined") {
        return null;
    }
    if (root instanceof HTMLElement || root instanceof Document || root instanceof ShadowRoot) {
        return root;
    }
    return (
        document.querySelector(".meeting-room-shell") ??
        document.querySelector("rtk-meeting") ??
        document.body
    );
}

/**
 * iOS: força sessão de conferência. Sem isso, com mic desligado (observador),
 * o interruptor silencioso / roteamento pode deixar o áudio remoto “mudo”.
 * Ref: Stream Video / WebKit #237322 — play-and-record.
 */
export function applyMobileAudioSession(): boolean {
    if (typeof navigator === "undefined") {
        return false;
    }
    const session = (navigator as AudioSessionNavigator).audioSession;
    if (!session || typeof session !== "object") {
        return false;
    }
    try {
        session.type = "play-and-record";
        return true;
    } catch {
        return false;
    }
}

async function applySinkIdIfPossible(media: HTMLMediaElement): Promise<boolean> {
    const anyMedia = media as HTMLMediaElement & {
        setSinkId?: (id: string) => Promise<void>;
        sinkId?: string;
    };
    if (typeof anyMedia.setSinkId !== "function") {
        return false;
    }
    try {
        await anyMedia.setSinkId("");
        return true;
    } catch {
        try {
            await anyMedia.setSinkId("default");
            return true;
        } catch {
            return false;
        }
    }
}

/**
 * Garante volume máximo e reprodução ativa nos elementos de mídia da sala
 * (incluindo Shadow DOM do RealtimeKit).
 */
export function boostRemoteMediaElements(root?: ParentNode | null): number {
    const scope = resolveBoostRoot(root);
    const mediaElements = collectMediaElementsDeep(scope);
    let played = 0;

    for (const media of mediaElements) {
        media.volume = 1;
        media.muted = false;
        media.setAttribute("playsinline", "");
        media.setAttribute("webkit-playsinline", "true");
        if (media.paused) {
            void media.play().then(() => {
                played += 1;
            }).catch(() => undefined);
        } else {
            played += 1;
        }
        void applySinkIdIfPossible(media);
    }

    return mediaElements.length;
}

async function pickSpeakerDevice(speakers: MediaDeviceInfo[]): Promise<MediaDeviceInfo | null> {
    if (!speakers.length) {
        return null;
    }

    const preferred =
        speakers.find((d) => /speaker|alto|loud|external|default/i.test(d.label)) ??
        speakers.find((d) => d.deviceId === "default") ??
        speakers[0];

    return preferred ?? null;
}

async function applySpeakerDevice(self: SelfWithSpeakerApi): Promise<boolean> {
    if (typeof self.getSpeakerDevices !== "function") {
        return false;
    }

    const speakers = await self.getSpeakerDevices();
    const preferred = await pickSpeakerDevice(speakers);
    if (!preferred) {
        return false;
    }

    if (typeof self.setSpeakerDevice === "function") {
        await self.setSpeakerDevice(preferred, { saveDevicePreference: false });
        return true;
    }

    if (typeof self.setDevice === "function") {
        await self.setDevice(preferred);
        return true;
    }

    return false;
}

/**
 * Pulso de mic: só funciona se o mic puder ser ligado. Observadores costumam
 * estar mutados — nesse caso usamos um getUserMedia curto só para “chutar”
 * o roteamento do iOS (e soltamos o track na hora).
 */
async function iosRouteKick(meetingClient: RealtimeKitClient): Promise<boolean> {
    const self = meetingClient.self;
    applyMobileAudioSession();

    if (self?.audioEnabled) {
        try {
            await self.disableAudio();
            await new Promise((resolve) => window.setTimeout(resolve, 120));
            await self.enableAudio();
            applyMobileAudioSession();
            return true;
        } catch {
            // cai no fallback getUserMedia
        }
    }

    if (!navigator.mediaDevices?.getUserMedia) {
        return false;
    }

    try {
        const stream = await navigator.mediaDevices.getUserMedia({
            audio: true,
            video: false,
        });
        stream.getTracks().forEach((t) => t.stop());
        applyMobileAudioSession();
        return true;
    } catch {
        return false;
    }
}

/**
 * No mobile, iOS/Android tendem a rotear WebRTC para o auricular.
 * Deve ser chamado preferencialmente após gesto do usuário (toque).
 */
export async function ensureMobileSpeakerOutput(
    meetingClient: RealtimeKitClient | null,
    options: MobileSpeakerOptions = {},
): Promise<SpeakerBoostResult> {
    const empty: SpeakerBoostResult = {
        ok: false,
        mediaElements: 0,
        played: 0,
        audioSessionSet: false,
        speakerDeviceSet: false,
        sinkIdApplied: 0,
        iosPulse: false,
        message: "Cliente de reunião indisponível",
    };

    if (!meetingClient) {
        return empty;
    }

    const audioSessionSet = applyMobileAudioSession();
    let speakerDeviceSet = false;
    const self = meetingClient.self as SelfWithSpeakerApi;

    try {
        if (typeof self.setupSpeaker === "function") {
            await self.setupSpeaker();
            speakerDeviceSet = true;
        } else {
            speakerDeviceSet = await applySpeakerDevice(self);
        }
    } catch {
        try {
            speakerDeviceSet = await applySpeakerDevice(self);
        } catch {
            speakerDeviceSet = false;
        }
    }

    const scope = resolveBoostRoot(options.root);
    const mediaElements = collectMediaElementsDeep(scope);
    let played = 0;
    let sinkIdApplied = 0;

    for (const media of mediaElements) {
        media.volume = 1;
        media.muted = false;
        media.setAttribute("playsinline", "");
        media.setAttribute("webkit-playsinline", "true");
        try {
            if (media.paused) {
                await media.play();
            }
            played += 1;
        } catch {
            // autoplay ainda bloqueado
        }
        if (await applySinkIdIfPossible(media)) {
            sinkIdApplied += 1;
        }
    }

    const isIos =
        typeof navigator !== "undefined" && /iPhone|iPad|iPod/i.test(navigator.userAgent);

    let iosPulse = false;
    if (options.iosMicPulse && isIos) {
        iosPulse = await iosRouteKick(meetingClient);
        // Re-play após o kick de rota
        for (const media of collectMediaElementsDeep(scope)) {
            media.muted = false;
            media.volume = 1;
            try {
                if (media.paused) {
                    await media.play();
                    played += 1;
                }
            } catch {
                // ignore
            }
        }
    }

    const found = Math.max(mediaElements.length, collectMediaElementsDeep(scope).length);
    const ok = played > 0 || speakerDeviceSet || audioSessionSet || iosPulse;

    let message: string;
    if (played > 0 && (audioSessionSet || speakerDeviceSet || iosPulse)) {
        message = "Áudio reativado. Aumente o volume do aparelho se ainda estiver baixo.";
    } else if (played > 0) {
        message = "Reprodução retomada. Se continuar baixo, aumente o volume lateral do celular.";
    } else if (found === 0) {
        message =
            "Não achei o player de áudio ainda. Abra a aba Videochamada e toque de novo em Ouvir alto.";
    } else {
        message =
            "O navegador bloqueou o áudio. Toque de novo em Ouvir alto com o volume do aparelho no máximo.";
    }

    return {
        ok,
        mediaElements: found,
        played,
        audioSessionSet,
        speakerDeviceSet,
        sinkIdApplied,
        iosPulse,
        message,
    };
}
